import { Map } from 'yandex-maps';
import { getBoundsCenter } from '@lib/map-helpers';
import { Sleeve, SleeveManager } from '@lib/sleeve-manager';
import { getBagPlace } from '@lib/bag-formatting';
import {
  $agricultureById,
  $agricultureIsLoading,
} from '@@/agriculture/store/agriculture.store';
import { IAgriculture } from '@@/agriculture/ts';
import {
  $zoomModeEnabled,
  onContextMenu,
  selectSleeve,
} from '@widgets/storage-viewer/model';
import type { SleeveDescription } from '@lib/sleeve-manager';
import { IWarehouse, IWarehouseWithBags } from '@@/warehouses/ts';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';

import styles from '@@/agriculture/components/agriculture-hint-paramaters/index.module.sass';
import { $mixedBags } from '@entities/simple-mix/model';

import { default as bunkerIcon } from '@/modules/common/icons/bunker.icon.svg';
import { IBag } from '@@/bags/ts';
import { isNull } from 'lodash-es';
import { WarehouseMap } from '@/widgets/storage-viewer/map/model';
import { notification } from 'antd';
import { IReceivingPoint } from '@/modules/seller-account/ts';

const DEFAULT_VIEWER_SETTINGS = {
  center: [45.04484, 38.97603],
  zoom: 8,
  zoom_default: 8,
  controls: ['zoomControl', 'fullscreenControl'],
};

const VIEWER_SETTINGS = 'VIEWER_SETTINGS';

const clientStorageService = DiContainer.get(ClientStorageService);

class PointsMap {
  private _map?: Map;
  private _marker?: any;
  private _container?: HTMLElement;
  private _containerId?: string;
  private _markers: { [whId: number | string]: ymaps.Placemark } = {};
  private _viewerSettings: typeof DEFAULT_VIEWER_SETTINGS = DEFAULT_VIEWER_SETTINGS;
  private _savedSettings: {
    type: ymaps.MapType;
    zoom: number;
    position: number[];
  } | null = null;
  private _sleeveManager: SleeveManager<SleeveDescription[]> | null = null;
  // private _sleeves: ymaps.Placemark[] = [];
  private _buildedSleeves: Sleeve[] = [];
  // private _descriptions: SleeveDescription[] = [];

  private initViewerSettings() {
    try {
      const storedViewerSettings = clientStorageService.get<string>(VIEWER_SETTINGS);

      this._viewerSettings = storedViewerSettings
        ? JSON.parse(storedViewerSettings)
        : DEFAULT_VIEWER_SETTINGS;
      clientStorageService.set(VIEWER_SETTINGS, this._viewerSettings);
    } catch (error) {
      this._viewerSettings = DEFAULT_VIEWER_SETTINGS;
    }
    if (!$zoomModeEnabled.getState() && this._viewerSettings.zoom_default) {
      this._viewerSettings.zoom = this._viewerSettings.zoom_default;
    }
  }

  private updateViewerSettings = () => {
    const center = this._map?.getCenter() ?? DEFAULT_VIEWER_SETTINGS.center;
    const zoom = this._map?.getZoom() ?? DEFAULT_VIEWER_SETTINGS.zoom;
    const settings = { ...this._viewerSettings, center, zoom };
    if (!$zoomModeEnabled.getState()) {
      settings.zoom_default = zoom;
    }
    clientStorageService.set(VIEWER_SETTINGS, settings);
  };

  init(id: string, container: HTMLElement | null) {
    if (!container) throw new Error('Не задан container');

    this._container = container;
    this._containerId = id;

    this.initViewerSettings();

    const map = new ymaps.Map(this._containerId, this._viewerSettings);

    this._map = map;

    map.events.add('actionend', this.updateViewerSettings);

    map.behaviors.disable('dblClickZoom');

    const geoLocationControl = new ymaps.control.GeolocationControl();
    map.controls.add(geoLocationControl)

    const viewTypeSelector = new ymaps.control.TypeSelector();
    map.controls.add(viewTypeSelector);
  }

  initClickEvent(setPointName: (obj: {
    //name: string;
    country: string;
    region: string;
    area: string;
    street?: string;
    house?: string;
    latitude: string;
    longitude: string;
    enabled?: number;
  }) => void, pointName: IReceivingPoint) {
    // setPointName({ ...pointName, street: '', house: '', area: '', region: '' });
    this._map?.events.add('click', (e) => this.onMapClick(e, { setPointName, pointName, }));
  }

  dispose() {
    if (this._map) {
      // console.log('Удаляем карту ...');
      this._map.destroy();
    }

    if (this._sleeveManager) this._sleeveManager.dispose();
    this._markers = {};
  }

  resize() {
    // console.log('resize');
    if (this._map) this._map.container.fitToViewport();
  }

  onMapClick(
    e: any,
    args: {
      setPointName: (obj: {
        country: string;
        region: string;
        area: string;
        street?: string;
        house?: string;
        latitude: string;
        longitude: string;
        enabled?: number;
        // name: string;
      }) => void,
      pointName: IReceivingPoint,
    }) {
    const coords = e.get('coords');
    if (this._marker) {
      this._map?.geoObjects.remove(this._marker);
    }

    this._marker = new ymaps.Placemark(
      coords,
      {
        hintContent: 'Маркер',
      },
      {
        preset: 'islands#redIcon',
      },
    );

    this._map?.geoObjects.add(this._marker);
    args.setPointName({ ...args.pointName, latitude: coords[0], longitude: coords[1] });
    const yGeocoder = ymaps.geocode(this._marker.geometry._coordinates);
    yGeocoder.then(
      (res: any) => {
        const address = res.geoObjects
          .get(0)
          .properties.get('metaDataProperty', {}).GeocoderMetaData;
        const processedAddress = this.getAddressDetails(address);
        processedAddress && args.setPointName({ ...processedAddress, latitude: this._marker.geometry._coordinates[0], longitude: this._marker.geometry._coordinates[1] });
      },
      function (err) {
        console.error(err);
        notification.error({ message: 'Сервис Geocoder временно недоступен' })
        // Обработка ошибки.
      },
    );
  }

  getAddressDetails(geocoderMetaData: any) {
    const addressDetails = geocoderMetaData.Address.Components;
    if (addressDetails) {
      const country = addressDetails.filter((item: any) => item.kind === 'country');
      const region = addressDetails.filter((item: any) => item.kind === 'province');
      const area = addressDetails.filter((item: any) => item.kind === 'locality') || addressDetails.filter((item: any) => item.kind === 'area') || '';
      const street = addressDetails.filter((item: any) => item.kind === 'street');
      const house = addressDetails.filter((item: any) => item.kind === 'house');
      const res: {
        country: string;
        region: string;
        area: string;
        street?: string;
        house?: string;
      } = {
        country: country[country.length === 1 ? 0 : country.length - 1]?.name || '',
        region: region[region.length === 1 ? 0 : region.length - 1]?.name || '',
        area: area[area.length === 1 ? 0 : area.length - 1]?.name || '',
        street: street[street.length === 1 ? 0 : street.length - 1]?.name || '',
        house: house[house.length === 1 ? 0 : house.length - 1]?.name || '',
      };
      return res;
    }

    return null;
  }

  async setMarkerByAddress(addressString: string, pointName: IReceivingPoint) {
    try {
      // Получаем координаты по адресу с использованием геокодера
      const response: any = await ymaps.geocode(addressString);
      const type = response.geoObjects?.get(0)?.properties?._data.metaDataProperty?.GeocoderMetaData?.kind
      const coordinates = type === 'house' ? response.geoObjects.get(0).geometry.getCoordinates() : [pointName.latitude, pointName.longitude];

      // Удаляем предыдущую метку, если она есть
      if (this._marker) {
        this._map?.geoObjects.remove(this._marker);
      }

      // Создаем новую метку по полученным координатам
      this._marker = new ymaps.Placemark(
        coordinates,
        {
          hintContent: 'Маркер',
        },
        {
          preset: 'islands#redIcon',
        }
      );

      // Добавляем метку на карту
      this._map?.geoObjects.add(this._marker);

      // Центрируем карту на новой метке
      this._map?.setCenter(coordinates, this._map.getZoom());

      return coordinates;
    } catch (error) {
      console.error('Ошибка при установке маркера:', error);
    }
  }

  setZoomToView() {
    if (this._map) {
      const currentZoom = this._map.getZoom();
      const newZoom = currentZoom * 2.3;
      this._map.setZoom(newZoom);
    }
  }

  setViewMode(mode: 'scheme' | 'satellite' | 'hybrid') {
    if (mode === 'scheme') {
      this._map?.setType('yandex#map');
    } else if (mode === 'hybrid') {
      this._map?.setType('yandex#hybrid');
    } else if (mode === 'satellite') {
      this._map?.setType('yandex#satellite');
    }
  }

}

export const pointsMap = new PointsMap();
